import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle, useContext } from "react";
import {
  CommandBar,
  ICommandBarItemProps,
  Dialog,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  DialogType,
  IModalProps
} from "@fluentui/react";
import { v4 } from "uuid";
import CustomButton from "../CustomButton";
import NotificationMessages, { INotificationMessagesRef } from "../NotificationMessages";

import excelFile from "../../../batch_template/SLBatch_DraftTemplate.xlsx";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";
import { SLActionStatus } from "../../utilities/utilities";

export type AllSLBatchActions = "post" | "downloadTemplate" | "clearForm";

export interface IFormProps {
  onCommand: (commandName: AllSLBatchActions, userText?: string) => any;
  children: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface BatchActionBarAndNotificationsRef extends INotificationMessagesRef {}

const SLBatchActionBar: React.ForwardRefExoticComponent<
  IFormProps & React.RefAttributes<BatchActionBarAndNotificationsRef>
> = forwardRef((props, ref) => {
  const slBatchContext = useContext(SLCreateSLBatchContext);
  const [hideDialogClear, setHideDialogClear] = useState(true);
  const [hideDialogPostToSap, setHideDialogPostToSap] = useState(true);

  const [commands, setCommands] = useState<ICommandBarItemProps[]>([]);
  const notificationsRef = useRef<INotificationMessagesRef>(null);

  useImperativeHandle(ref, () => ({
    setMessage(type, messages) {
      if (notificationsRef.current) {
        notificationsRef.current.setMessage(type, messages);
      }
    },
    reset() {
      if (notificationsRef.current) {
        notificationsRef.current.reset();
      }
    }
  }));

  const closeClearDialog = () => {
    setHideDialogClear(true);
  };
  const closePostToSapDialog = () => {
    setHideDialogPostToSap(true);
  };

  const clearStoreAction = async (ev: any) => {
    ev.preventDefault();
    props.onCommand("clearForm");
    setHideDialogClear(true);
  };

  const postToSapAction = async (ev: any) => {
    ev.preventDefault();
    props.onCommand("post");
    setHideDialogPostToSap(true);
  };

  useEffect(() => {
    const commandBarItems: ICommandBarItemProps[] = [
      {
        key: "post",
        name: "Post",
        iconProps: { iconName: "Mail" },
        onClick: () => setHideDialogPostToSap(false),
        disabled: slBatchContext.getPermission("post") === SLActionStatus.Disabled
      },
      {
        key: "clearForm",
        name: "Clear Form",
        iconProps: { iconName: "ClearFilter" },
        onClick: () => setHideDialogClear(false),
        disabled: slBatchContext.getPermission("clearForm") === SLActionStatus.Disabled
      },
      {
        key: "downloadTemplate",
        name: "Download Template",
        iconProps: { iconName: "DownloadDocument" },
        href: excelFile
      }
    ];
    setCommands(commandBarItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slBatchContext.loadingStatus]);

  const dialogModalProps = {
    isBlocking: true,
    styles: { main: { maxWidth: 450 } }
  } as IModalProps;

  const id = v4();

  return (
    <React.Fragment>
      <form id={id} action="#" aria-label="Subledger batch actions">
        <NotificationMessages ref={notificationsRef} />
        <Dialog
          hidden={hideDialogPostToSap}
          onDismiss={closePostToSapDialog}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Post to SAP",
            subText: "Would you like to save data and post to SAP?"
          }}
          modalProps={dialogModalProps}
        >
          <DialogFooter>
            <PrimaryButton onClick={postToSapAction} text="Post to SAP" />
            <DefaultButton onClick={closePostToSapDialog} text="Cancel" />
          </DialogFooter>
        </Dialog>
        <Dialog
          hidden={hideDialogClear}
          onDismiss={closeClearDialog}
          dialogContentProps={{
            type: DialogType.normal,
            title: "Clear Form",
            subText: "Would you like to reset this form?"
          }}
          modalProps={dialogModalProps}
        >
          <DialogFooter>
            <PrimaryButton onClick={clearStoreAction} text="Clear Form" />
            <DefaultButton onClick={closeClearDialog} text="Cancel" />
          </DialogFooter>
        </Dialog>
        <CommandBar
          buttonAs={CustomButton}
          items={commands}
          ariaLabel={"Actions for SLEntry"}
          overflowButtonProps={{ ariaLabel: "More Actions" }}
        />
        {props.children}
      </form>
    </React.Fragment>
  );
});

export default SLBatchActionBar;
